import React, { useState, useEffect, useCallback } from 'react';
import { DigisacService } from '../../services/digisacService';
import { useNotifier } from '../ui/Notifier';
import type { DigisacMessage } from '../../types/digisac';

interface MessageTableProps {
  refreshKey?: number;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#F59E0B',
  sent: '#3B82F6',
  delivered: '#8B5CF6',
  read: '#10B981',
  failed: '#EF4444',
};

export const MessageTable: React.FC<MessageTableProps> = ({ refreshKey }) => {
  const notifier = useNotifier();
  const [messages, setMessages] = useState<DigisacMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DigisacService.listMessages();
      const list = Array.isArray(data) ? data : (data as any)?.data ?? [];
      setMessages(list);
    } catch (err: any) {
      notifier.show({ message: err?.message ?? 'Erro ao carregar mensagens', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notifier]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Poll pending/sent messages every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      const pending = messages.filter(m => m.status === 'pending' || m.status === 'sent');
      for (const msg of pending) {
        try {
          const updated = await DigisacService.syncMessageStatus(msg.id);
          setMessages(prev =>
            prev.map(m => m.id === msg.id ? (updated as DigisacMessage) : m)
          );
        } catch {
          // silently ignore polling errors
        }
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [messages]);

  const handleSync = async (id: string) => {
    setSyncing(id);
    try {
      const updated = await DigisacService.syncMessageStatus(id);
      setMessages(prev => prev.map(m => m.id === id ? (updated as DigisacMessage) : m));
      notifier.show({ message: 'Status atualizado', severity: 'success' });
    } catch (err: any) {
      notifier.show({ message: err?.message ?? 'Erro ao sincronizar status', severity: 'error' });
    } finally {
      setSyncing(null);
    }
  };

  if (loading) {
    return <p className="text-xs text-gray-500 py-4 text-center">Carregando mensagens...</p>;
  }

  if (messages.length === 0) {
    return <p className="text-xs text-gray-400 py-4 text-center">Nenhuma mensagem enviada ainda.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b" style={{ color: '#3d2b56' }}>
            <th className="text-left py-2 px-3 font-semibold">Telefone</th>
            <th className="text-left py-2 px-3 font-semibold">Tipo</th>
            <th className="text-left py-2 px-3 font-semibold">Mensagem</th>
            <th className="text-left py-2 px-3 font-semibold">Status</th>
            <th className="text-left py-2 px-3 font-semibold">Enviado em</th>
            <th className="text-left py-2 px-3 font-semibold">Ações</th>
          </tr>
        </thead>
        <tbody>
          {messages.map(msg => (
            <tr key={msg.id} className="border-b hover:bg-gray-50">
              <td className="py-2 px-3 font-mono">{msg.contact?.phoneE164 ?? '—'}</td>
              <td className="py-2 px-3 capitalize">{msg.tipo}</td>
              <td className="py-2 px-3 max-w-xs truncate" title={msg.conteudo}>{msg.conteudo}</td>
              <td className="py-2 px-3">
                <span
                  className="px-2 py-0.5 rounded-full text-white text-xs"
                  style={{ backgroundColor: STATUS_COLORS[msg.status] ?? '#9CA3AF' }}
                >
                  {STATUS_LABELS[msg.status] ?? msg.status}
                </span>
              </td>
              <td className="py-2 px-3 text-gray-500">
                {msg.enviadoEm ? new Date(msg.enviadoEm).toLocaleString('pt-BR') : '—'}
              </td>
              <td className="py-2 px-3">
                {(msg.status === 'pending' || msg.status === 'sent') && (
                  <button
                    onClick={() => handleSync(msg.id)}
                    disabled={syncing === msg.id}
                    className="text-xs px-2 py-1 rounded text-white disabled:opacity-50"
                    style={{ backgroundColor: '#7A61B2' }}
                  >
                    {syncing === msg.id ? '...' : 'Sync'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
